import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from "../../components/AccessRestricted";
import {
  ChevronLeft,
  Save,
  Loader2,
  History,
  Plus,
  Minus,
  CheckCircle2,
  AlertCircle
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "../../components/ui/card";
import api from "../../utils/api";
import { addDecimals, formatDecimal, DECIMAL_INPUT_PATTERN } from "../../utils/decimal";
import { toast } from "sonner";

interface EmployeeDetails {
  id: string;
  name: string;
  department: string;
}

interface LeaveBalanceDetail {
  quota: string;
  used: string;
  adjustment: string;
  remaining: string;
}

interface Balances {
  [key: string]: LeaveBalanceDetail;
}

interface AdjustmentRecord {
  id: string;
  leaveType: string;
  adjustmentHours: string;
  reason: string;
  createdAt: string;
  adjustedBy: {
    name: string;
  } | null;
}

const AdjustLeaveBalance = () => {
  const { employeeId } = useParams<{ employeeId: string }>();
  const navigate = useNavigate();
  const ability = useAbility(AbilityContext);
  const canManage = ability.can(ACTIONS.MANAGE, RESOURCES.ATTENDANCE_ADMIN)
    || ability.can(ACTIONS.MANAGE, RESOURCES.LEAVE_BALANCES_TAB);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [employee, setEmployee] = useState<EmployeeDetails | null>(null);
  const [balances, setBalances] = useState<Balances | null>(null);
  const [history, setHistory] = useState<AdjustmentRecord[]>([]);

  const [adjustments, setAdjustments] = useState({
    "Annual Leave": "",
    "Personal Leave": ""
  });
  const [reason, setReason] = useState("");

  const fetchData = async () => {
    if (!employeeId) return;

    setLoading(true);
    try {
      const [detailsRes, historyRes] = await Promise.all([
        api.get(`/api/attendance/employee-leave-details/${employeeId}`),
        api.get(`/api/attendance/leave-adjustments?employeeId=${employeeId}`)
      ]);

      if (detailsRes.success) {
        setEmployee(detailsRes.data.employee);
        setBalances(detailsRes.data.balances);
      }

      if (historyRes.success) {
        setHistory(historyRes.data);
      }
    } catch (error) {
      console.error("Error fetching adjustment data:", error);
      toast.error("Failed to load employee data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [employeeId]);

  const handleAdjustmentChange = (type: string, value: string) => {
    // Keep the raw input so full decimal precision (up to 14 dp) can be entered
    setAdjustments(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const calculateNewBalance = (type: string) => {
    if (!balances) return "0";
    // Exact decimal addition on the full-precision numbers
    return addDecimals(balances[type].remaining, adjustments[type as keyof typeof adjustments] || "0");
  };

  const handleSubmit = async () => {
    if (!employeeId) return;
    if (!reason.trim()) {
      toast.error("Please provide a reason for the adjustment");
      return;
    }

    for (const [, value] of Object.entries(adjustments)) {
      const trimmed = value.trim();
      if (trimmed !== "" && !DECIMAL_INPUT_PATTERN.test(trimmed)) {
        toast.error("Adjustment hours must be a number with up to 14 decimal places");
        return;
      }
    }

    const activeAdjustments = Object.entries(adjustments)
      .filter(([_, value]) => value.trim() !== "" && Number(value) !== 0)
      .map(([type, hours]) => ({ type, hours: hours.trim() }));

    if (activeAdjustments.length === 0) {
      toast.error("Please enter at least one adjustment value");
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post("/api/attendance/adjust-balance", {
        employeeId,
        reason: reason.trim(),
        adjustments: activeAdjustments
      });

      if (res.success) {
        toast.success("Balances adjusted successfully");
        setAdjustments({
          "Annual Leave": "",
          "Personal Leave": ""
        });
        setReason("");
        fetchData(); // Refresh data
      } else {
        toast.error(res.message || "Failed to adjust balance");
      }
    } catch (error) {
      console.error("Error submitting adjustments:", error);
      toast.error("An error occurred while saving adjustments");
    } finally {
      setSubmitting(false);
    }
  };

  if (!canManage) {
    return (
      <AccessRestricted
        accessMessage="You don't have permission to adjust leave balances."
        backMessage="Back to HR Admin"
        navigateTo="/attendance/admin"
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!employee || !balances) {
    return (
      <div className="text-center py-20">
        <h2 className="text-xl font-bold text-gray-900">Employee not found</h2>
        <Button
          variant="link"
          onClick={() => navigate("/attendance/admin")}
          className="mt-4"
        >
          Return to Admin
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-[1000px] mx-auto pb-20 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button
          variant="outline"
          size="icon"
          className="rounded-xl border-gray-100 bg-white hover:bg-gray-50 shrink-0"
          onClick={() => navigate("/attendance/admin")}
        >
          <ChevronLeft size={20} />
        </Button>
        <div className="flex items-center gap-4">
          <Avatar className="h-12 w-12 border border-gray-100 shadow-sm">
            <AvatarFallback className="bg-gray-50 text-gray-700 font-bold">
              {employee.name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)}
            </AvatarFallback>
          </Avatar>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Adjust Leave Balance</h1>
            <p className="text-gray-500 font-medium text-sm">
              Current and historical adjustments for <span className="text-gray-900 font-bold">{employee.name}</span> • {employee.department}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Adjustment Form */}
        <div className="lg:col-span-2 space-y-8">
          <Card className="rounded-[24px] border-gray-100 shadow-sm overflow-hidden">
            <CardHeader className="bg-gray-50/50 border-b border-gray-100 p-6">
              <CardTitle className="text-base font-bold text-gray-900">Adjustment Details</CardTitle>
              <CardDescription className="text-gray-500">Enter adjustment hours at full precision (e.g., 2 or -1.53846153846154) and calculate new balances.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-white border-b border-gray-100 text-left">
                      <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Leave Type</th>
                      <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-center">Current</th>
                      <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-center">Adjustment</th>
                      <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-center">New Balance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {Object.keys(balances).map((type) => {
                      const adjustment = adjustments[type as keyof typeof adjustments];
                      const newBalance = calculateNewBalance(type);

                      return (
                        <tr key={type} className="hover:bg-gray-50/30 transition-colors">
                          <td className="px-6 py-5">
                            <span className="text-sm font-bold text-gray-900">{type}</span>
                          </td>
                          <td className="px-6 py-5 text-center font-bold text-gray-700">
                            {balances[type].remaining}
                          </td>
                          <td className="px-6 py-5">
                            <div className="flex items-center justify-center max-w-[160px] mx-auto">
                              <Input
                                type="number"
                                step="any"
                                value={adjustment}
                                onChange={(e) => handleAdjustmentChange(type, e.target.value)}
                                className="h-10 text-center font-bold rounded-xl border-gray-200 focus:ring-0 focus:border-black"
                                placeholder="0"
                              />
                            </div>
                          </td>
                          <td className="px-6 py-5 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <span className="text-sm font-black text-gray-900">
                                {newBalance} hours
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="p-6 border-t border-gray-100 space-y-4">
                <div>
                  <label className="text-sm font-bold text-gray-900 block mb-2">Adjustment Reason *</label>
                  <Textarea
                    placeholder="Provide a detailed reason for this adjustment (e.g., Carry-over from previous year, Correction of manual reporting error)"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="min-h-[120px] rounded-2xl border-gray-200 focus:ring-0 focus:border-black resize-none"
                  />
                </div>

                <div className="flex justify-end pt-4">
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="bg-black text-white hover:bg-gray-800 rounded-xl h-12 px-8 font-bold gap-2 transition-all shadow-lg shadow-black/10 disabled:opacity-50"
                  >
                    {submitting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Save className="h-4 w-4" />
                        Apply Adjustment
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Audit Trail (Sticky Sidebar or below on mobile) */}
        <div className="lg:col-span-1">
          <Card className="rounded-[24px] border-gray-100 shadow-sm sticky top-8">
            <CardHeader className="bg-gray-50/50 border-b border-gray-100 p-6">
              <div className="flex items-center gap-2">
                <History className="h-4 w-4 text-gray-500" />
                <CardTitle className="text-base font-bold text-gray-900">Audit Trail</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-0 max-h-[600px] overflow-y-auto">
              {history.length > 0 ? (
                <div className="p-6 space-y-6">
                  {history.map((record) => (
                    <div key={record.id} className="relative pl-6 pb-6 border-l-2 border-gray-100 last:pb-0 last:border-0">
                      <div className="absolute left-[-9px] top-0 h-4 w-4 rounded-full bg-white border-2 border-gray-200" />
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-xs font-black uppercase tracking-wider ${record.leaveType === "Annual Leave" ? "text-green-600" : record.leaveType === "Unpaid Leave" ? "text-orange-600" : "text-blue-600"
                            }`}>
                            {record.leaveType}
                          </span>
                          <span className="text-[10px] font-bold text-gray-400">
                            {new Date(record.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-sm font-bold text-gray-900">
                          {!String(record.adjustmentHours).startsWith("-") ? "+" : ""}{formatDecimal(record.adjustmentHours)} hours
                        </p>
                        <p className="text-xs text-gray-500 font-medium mt-1 italic line-clamp-2" title={record.reason}>
                          "{record.reason}"
                        </p>
                        <div className="mt-2 flex items-center gap-1.5 grayscale opacity-70">
                          <Avatar className="h-4 w-4">
                            <AvatarFallback className="text-[8px]">{record.adjustedBy?.name?.[0] ?? "S"}</AvatarFallback>
                          </Avatar>
                          <span className="text-[10px] font-bold text-gray-400">By {record.adjustedBy?.name ?? "System"}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-12 text-center">
                  <AlertCircle className="h-8 w-8 text-gray-100 mx-auto mb-3" />
                  <p className="text-sm text-gray-400 font-medium">No historical adjustments found.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdjustLeaveBalance;
